import { useRef } from "react";
import { RotateCw, Maximize2 } from "lucide-react";
import { stickerUrl } from "@/components/editor/stickers/fx";

const SIZE_K = { s: 0.72, m: 1, l: 1.32 };
const POS_XY = { center: [0.5, 0.5], top: [0.5, 0.3], bottom: [0.5, 0.72], left: [0.25, 0.5], right: [0.75, 0.5] };

export const toFree = (s) => {
  if (s.x != null && s.y != null) return s;
  const [x, y] = POS_XY[s.pos] || POS_XY.center;
  const k = (["left", "right"].includes(s.pos) ? 1 : 1.55) * (SIZE_K[s.size] || 1);
  return { ...s, x, y, scale: +Math.min(3, k).toFixed(2), rot: 0 };
};

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const Item = ({ s, i, box, editable, selected, onSelect, onChange }) => {
  const f = toFree(s);
  const start = useRef(null);
  const down = (mode) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(i);
    const r = box.current.getBoundingClientRect();
    const cx = r.left + f.x * r.width, cy = r.top + f.y * r.height;
    const eb = e.currentTarget.getBoundingClientRect();
    start.current = { mode, r, cx, cy, px: e.clientX, py: e.clientY, x: f.x, y: f.y, scale: f.scale, rot: f.rot, ew: eb.width, eh: eb.height,
      dist: Math.hypot(e.clientX - cx, e.clientY - cy) || 1, ang: Math.atan2(e.clientY - cy, e.clientX - cx) };
    const move = (ev) => {
      const st = start.current;
      if (st.mode === "move") {
        const hw = Math.min(0.45, st.ew / 2 / st.r.width), hh = Math.min(0.45, st.eh / 2 / st.r.height);
        onChange(i, { x: +clamp(st.x + (ev.clientX - st.px) / st.r.width, hw, 1 - hw).toFixed(3), y: +clamp(st.y + (ev.clientY - st.py) / st.r.height, hh, 1 - hh).toFixed(3) });
      }
      if (st.mode === "scale") onChange(i, { scale: +clamp(st.scale * Math.hypot(ev.clientX - st.cx, ev.clientY - st.cy) / st.dist, 0.2, 3).toFixed(2) });
      if (st.mode === "rot") {
        let a = st.rot + ((Math.atan2(ev.clientY - st.cy, ev.clientX - st.cx) - st.ang) * 180) / Math.PI;
        a = ((a + 540) % 360) - 180;
        onChange(i, { rot: Math.round(Math.abs(a) < 4 ? 0 : a) });
      }
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div className={`absolute ${editable ? "pointer-events-auto cursor-move touch-none" : "pointer-events-none"}`} onPointerDown={down("move")} data-testid={`stage-sticker-${i}`}
      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${Math.min(150, 40 * f.scale)}%`, transform: `translate(-50%, -50%) rotate(${f.rot}deg)` }}>
      <div className={editable ? "" : `cx-st cx-st-${s.anim || "pop"}`} style={{ "--d": `${s.d || 1.6}s`, "--m": `url("${stickerUrl(s)}") center / 100% 100% no-repeat` }} data-testid="fx-preview-sticker" data-anim={s.anim || "pop"}>
        <img src={stickerUrl(s)} alt="" draggable={false} className="w-full select-none drop-shadow-[0_10px_14px_rgba(0,0,0,0.65)]" />
      </div>
      {editable && selected && (
        <>
          <div className="absolute -inset-1 border-2 border-dashed border-[#3ad7ff] rounded pointer-events-none" />
          <button type="button" onPointerDown={down("scale")} data-testid={`stage-sticker-scale-${i}`} className="absolute -right-3 -bottom-3 w-6 h-6 rounded-full bg-[#3ad7ff] text-black grid place-items-center cursor-nwse-resize"><Maximize2 className="w-3 h-3" /></button>
          <button type="button" onPointerDown={down("rot")} data-testid={`stage-sticker-rotate-${i}`} className="absolute left-1/2 -top-8 -translate-x-1/2 w-6 h-6 rounded-full bg-[#a746ff] text-white grid place-items-center cursor-grab"><RotateCw className="w-3 h-3" /></button>
        </>
      )}
    </div>
  );
};

export const StickerLayer = ({ cfg, setCfg, rel, inSel, playing, box, selected, setSelected }) => {
  const list = cfg.stickers || [];
  const onChange = (i, patch) => setCfg((c) => ({ ...c, stickers: c.stickers.map((x, j) => (j === i ? { ...toFree(x), ...patch } : x)) }));
  return list.map((s, i) => {
    const visible = (inSel && rel >= s.t && rel < s.t + (s.d || 1.6)) || (!playing && selected === i);
    if (!visible) return null;
    return <Item key={`${i}-${s.id}-${s.anim}-${s.t}`} s={s} i={i} box={box} editable={!playing && !!setCfg} selected={selected === i} onSelect={setSelected} onChange={onChange} />;
  });
};
